import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Type, Copy, Shuffle, Sparkles, Star, Crown } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface TextStyle {
  name: string;
  converter: (text: string) => string;
  example: string;
  icon: React.ReactNode;
}

const StylishTextGenerator: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [generatedTexts, setGeneratedTexts] = useState<{ style: string; text: string }[]>([]);

  useSEO({
    title: 'Stylish Text Generator - Create Fancy Text for Social Media',
    description: 'Generate stylish and fancy text for social media, bios, and posts. Create unique text styles with special characters and symbols.',
    keywords: 'stylish text generator, fancy text, unicode text, social media text, cool text, text styles',
    canonical: 'https://snap-tools.online/social/stylish-text-generator'
  });

  const textStyles: TextStyle[] = [
    {
      name: 'Bold Serif',
      converter: (text) => text.replace(/[a-zA-Z0-9]/g, (char) => {
        const boldMap: { [key: string]: string } = {
          'a': '𝐚', 'b': '𝐛', 'c': '𝐜', 'd': '𝐝', 'e': '𝐞', 'f': '𝐟', 'g': '𝐠', 'h': '𝐡', 'i': '𝐢', 'j': '𝐣',
          'k': '𝐤', 'l': '𝐥', 'm': '𝐦', 'n': '𝐧', 'o': '𝐨', 'p': '𝐩', 'q': '𝐪', 'r': '𝐫', 's': '𝐬', 't': '𝐭',
          'u': '𝐮', 'v': '𝐯', 'w': '𝐰', 'x': '𝐱', 'y': '𝐲', 'z': '𝐳',
          'A': '𝐀', 'B': '𝐁', 'C': '𝐂', 'D': '𝐃', 'E': '𝐄', 'F': '𝐅', 'G': '𝐆', 'H': '𝐇', 'I': '𝐈', 'J': '𝐉',
          'K': '𝐊', 'L': '𝐋', 'M': '𝐌', 'N': '𝐍', 'O': '𝐎', 'P': '𝐏', 'Q': '𝐐', 'R': '𝐑', 'S': '𝐒', 'T': '𝐓',
          'U': '𝐔', 'V': '𝐕', 'W': '𝐖', 'X': '𝐗', 'Y': '𝐘', 'Z': '𝐙',
          '0': '𝟎', '1': '𝟏', '2': '𝟐', '3': '𝟑', '4': '𝟒', '5': '𝟓', '6': '𝟔', '7': '𝟕', '8': '𝟖', '9': '𝟗'
        };
        return boldMap[char] || char;
      }),
      example: '𝐒𝐭𝐲𝐥𝐢𝐬𝐡 𝐓𝐞𝐱𝐭',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Italic',
      converter: (text) => text.replace(/[a-zA-Z]/g, (char) => {
        const italicMap: { [key: string]: string } = {
          'a': '𝑎', 'b': '𝑏', 'c': '𝑐', 'd': '𝑑', 'e': '𝑒', 'f': '𝑓', 'g': '𝑔', 'h': 'ℎ', 'i': '𝑖', 'j': '𝑗',
          'k': '𝑘', 'l': '𝑙', 'm': '𝑚', 'n': '𝑛', 'o': '𝑜', 'p': '𝑝', 'q': '𝑞', 'r': '𝑟', 's': '𝑠', 't': '𝑡',
          'u': '𝑢', 'v': '𝑣', 'w': '𝑤', 'x': '𝑥', 'y': '𝑦', 'z': '𝑧',
          'A': '𝐴', 'B': '𝐵', 'C': '𝐶', 'D': '𝐷', 'E': '𝐸', 'F': '𝐹', 'G': '𝐺', 'H': '𝐻', 'I': '𝐼', 'J': '𝐽',
          'K': '𝐾', 'L': '𝐿', 'M': '𝑀', 'N': '𝑁', 'O': '𝑂', 'P': '𝑃', 'Q': '𝑄', 'R': '𝑅', 'S': '𝑆', 'T': '𝑇',
          'U': '𝑈', 'V': '𝑉', 'W': '𝑊', 'X': '𝑋', 'Y': '𝑌', 'Z': '𝑍'
        };
        return italicMap[char] || char;
      }),
      example: '𝑆𝑡𝑦𝑙𝑖𝑠ℎ 𝑇𝑒𝑥𝑡',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Monospace',
      converter: (text) => text.replace(/[a-zA-Z0-9]/g, (char) => {
        const monoMap: { [key: string]: string } = {
          'a': '𝚊', 'b': '𝚋', 'c': '𝚌', 'd': '𝚍', 'e': '𝚎', 'f': '𝚏', 'g': '𝚐', 'h': '𝚑', 'i': '𝚒', 'j': '𝚓',
          'k': '𝚔', 'l': '𝚕', 'm': '𝚖', 'n': '𝚗', 'o': '𝚘', 'p': '𝚙', 'q': '𝚚', 'r': '𝚛', 's': '𝚜', 't': '𝚝',
          'u': '𝚞', 'v': '𝚟', 'w': '𝚠', 'x': '𝚡', 'y': '𝚢', 'z': '𝚣',
          'A': '𝙰', 'B': '𝙱', 'C': '𝙲', 'D': '𝙳', 'E': '𝙴', 'F': '𝙵', 'G': '𝙶', 'H': '𝙷', 'I': '𝙸', 'J': '𝙹',
          'K': '𝙺', 'L': '𝙻', 'M': '𝙼', 'N': '𝙽', 'O': '𝙾', 'P': '𝙿', 'Q': '𝚀', 'R': '𝚁', 'S': '𝚂', 'T': '𝚃',
          'U': '𝚄', 'V': '𝚅', 'W': '𝚆', 'X': '𝚇', 'Y': '𝚈', 'Z': '𝚉',
          '0': '𝟶', '1': '𝟷', '2': '𝟸', '3': '𝟹', '4': '𝟺', '5': '𝟻', '6': '𝟼', '7': '𝟽', '8': '𝟾', '9': '𝟿'
        };
        return monoMap[char] || char;
      }),
      example: '𝚂𝚝𝚢𝚕𝚒𝚜𝚑 𝚃𝚎𝚡𝚝',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Sans Serif',
      converter: (text) => text.replace(/[a-zA-Z0-9]/g, (char) => {
        const sansMap: { [key: string]: string } = {
          'a': '𝖺', 'b': '𝖻', 'c': '𝖼', 'd': '𝖽', 'e': '𝖾', 'f': '𝖿', 'g': '𝗀', 'h': '𝗁', 'i': '𝗂', 'j': '𝗃',
          'k': '𝗄', 'l': '𝗅', 'm': '𝗆', 'n': '𝗇', 'o': '𝗈', 'p': '𝗉', 'q': '𝗊', 'r': '𝗋', 's': '𝗌', 't': '𝗍',
          'u': '𝗎', 'v': '𝗏', 'w': '𝗐', 'x': '𝗑', 'y': '𝗒', 'z': '𝗓',
          'A': '𝖠', 'B': '𝖡', 'C': '𝖢', 'D': '𝖣', 'E': '𝖤', 'F': '𝖥', 'G': '𝖦', 'H': '𝖧', 'I': '𝖨', 'J': '𝖩',
          'K': '𝖪', 'L': '𝖫', 'M': '𝖬', 'N': '𝖭', 'O': '𝖮', 'P': '𝖯', 'Q': '𝖰', 'R': '𝖱', 'S': '𝖲', 'T': '𝖳',
          'U': '𝖴', 'V': '𝖵', 'W': '𝖶', 'X': '𝖷', 'Y': '𝖸', 'Z': '𝖹',
          '0': '𝟢', '1': '𝟣', '2': '𝟤', '3': '𝟥', '4': '𝟦', '5': '𝟧', '6': '𝟨', '7': '𝟩', '8': '𝟪', '9': '𝟫'
        };
        return sansMap[char] || char;
      }),
      example: '𝖲𝗍𝗒𝗅𝗂𝗌𝗁 𝖳𝖾𝗑𝗍',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Double Struck',
      converter: (text) => text.replace(/[a-zA-Z0-9]/g, (char) => {
        const doubleMap: { [key: string]: string } = {
          'a': '𝕒', 'b': '𝕓', 'c': '𝕔', 'd': '𝕕', 'e': '𝕖', 'f': '𝕗', 'g': '𝕘', 'h': '𝕙', 'i': '𝕚', 'j': '𝕛',
          'k': '𝕜', 'l': '𝕝', 'm': '𝕞', 'n': '𝕟', 'o': '𝕠', 'p': '𝕡', 'q': '𝕢', 'r': '𝕣', 's': '𝕤', 't': '𝕥',
          'u': '𝕦', 'v': '𝕧', 'w': '𝕨', 'x': '𝕩', 'y': '𝕪', 'z': '𝕫',
          'A': '𝔸', 'B': '𝔹', 'C': 'ℂ', 'D': '𝔻', 'E': '𝔼', 'F': '𝔽', 'G': '𝔾', 'H': 'ℍ', 'I': '𝕀', 'J': '𝕁',
          'K': '𝕂', 'L': '𝕃', 'M': '𝕄', 'N': 'ℕ', 'O': '𝕆', 'P': 'ℙ', 'Q': 'ℚ', 'R': 'ℝ', 'S': '𝕊', 'T': '𝕋',
          'U': '𝕌', 'V': '𝕍', 'W': '𝕎', 'X': '𝕏', 'Y': '𝕐', 'Z': 'ℤ',
          '0': '𝟘', '1': '𝟙', '2': '𝟚', '3': '𝟛', '4': '𝟜', '5': '𝟝', '6': '𝟞', '7': '𝟟', '8': '𝟠', '9': '𝟡'
        };
        return doubleMap[char] || char;
      }),
      example: '𝕊𝕥𝕪𝕝𝕚𝕤𝕙 𝕋𝕖𝕩𝕥',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Cursive',
      converter: (text) => text.replace(/[a-zA-Z]/g, (char) => {
        const cursiveMap: { [key: string]: string } = {
          'a': '𝒶', 'b': '𝒷', 'c': '𝒸', 'd': '𝒹', 'e': 'ℯ', 'f': '𝒻', 'g': 'ℊ', 'h': '𝒽', 'i': '𝒾', 'j': '𝒿',
          'k': '𝓀', 'l': '𝓁', 'm': '𝓂', 'n': '𝓃', 'o': 'ℴ', 'p': '𝓅', 'q': '𝓆', 'r': '𝓇', 's': '𝓈', 't': '𝓉',
          'u': '𝓊', 'v': '𝓋', 'w': '𝓌', 'x': '𝓍', 'y': '𝓎', 'z': '𝓏',
          'A': '𝒜', 'B': 'ℬ', 'C': '𝒞', 'D': '𝒟', 'E': 'ℰ', 'F': 'ℱ', 'G': '𝒢', 'H': 'ℋ', 'I': 'ℐ', 'J': '𝒥',
          'K': '𝒦', 'L': 'ℒ', 'M': 'ℳ', 'N': '𝒩', 'O': '𝒪', 'P': '𝒫', 'Q': '𝒬', 'R': 'ℛ', 'S': '𝒮', 'T': '𝒯',
          'U': '𝒰', 'V': '𝒱', 'W': '𝒲', 'X': '𝒳', 'Y': '𝒴', 'Z': '𝒵'
        };
        return cursiveMap[char] || char;
      }),
      example: '𝒮𝓉𝓎𝓁𝒾𝓈𝒽 𝒯ℯ𝓍𝓉',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Bubble Text',
      converter: (text) => text.replace(/[a-zA-Z0-9]/g, (char) => {
        const bubbleMap: { [key: string]: string } = {
          'a': 'ⓐ', 'b': 'ⓑ', 'c': 'ⓒ', 'd': 'ⓓ', 'e': 'ⓔ', 'f': 'ⓕ', 'g': 'ⓖ', 'h': 'ⓗ', 'i': 'ⓘ', 'j': 'ⓙ',
          'k': 'ⓚ', 'l': 'ⓛ', 'm': 'ⓜ', 'n': 'ⓝ', 'o': 'ⓞ', 'p': 'ⓟ', 'q': 'ⓠ', 'r': 'ⓡ', 's': 'ⓢ', 't': 'ⓣ',
          'u': 'ⓤ', 'v': 'ⓥ', 'w': 'ⓦ', 'x': 'ⓧ', 'y': 'ⓨ', 'z': 'ⓩ',
          'A': 'Ⓐ', 'B': 'Ⓑ', 'C': 'Ⓒ', 'D': 'Ⓓ', 'E': 'Ⓔ', 'F': 'Ⓕ', 'G': 'Ⓖ', 'H': 'Ⓗ', 'I': 'Ⓘ', 'J': 'Ⓙ',
          'K': 'Ⓚ', 'L': 'Ⓛ', 'M': 'Ⓜ', 'N': 'Ⓝ', 'O': 'Ⓞ', 'P': 'Ⓟ', 'Q': 'Ⓠ', 'R': 'Ⓡ', 'S': 'Ⓢ', 'T': 'Ⓣ',
          'U': 'Ⓤ', 'V': 'Ⓥ', 'W': 'Ⓦ', 'X': 'Ⓧ', 'Y': 'Ⓨ', 'Z': 'Ⓩ',
          '0': '⓪', '1': '①', '2': '②', '3': '③', '4': '④', '5': '⑤', '6': '⑥', '7': '⑦', '8': '⑧', '9': '⑨'
        };
        return bubbleMap[char] || char;
      }),
      example: 'Ⓢⓣⓨⓛⓘⓢⓗ Ⓣⓔⓧⓣ',
      icon: <Crown className="w-4 h-4" />
    },
    {
      name: 'Square Text',
      converter: (text) => text.replace(/[a-zA-Z0-9]/g, (char) => {
        const squareMap: { [key: string]: string } = {
          'a': '🅰', 'b': '🅱', 'c': '🅲', 'd': '🅳', 'e': '🅴', 'f': '🅵', 'g': '🅶', 'h': '🅷', 'i': '🅸', 'j': '🅹',
          'k': '🅺', 'l': '🅻', 'm': '🅼', 'n': '🅽', 'o': '🅾', 'p': '🅿', 'q': '🆀', 'r': '🆁', 's': '🆂', 't': '🆃',
          'u': '🆄', 'v': '🆅', 'w': '🆆', 'x': '🆇', 'y': '🆈', 'z': '🆉',
          'A': '🅰', 'B': '🅱', 'C': '🅲', 'D': '🅳', 'E': '🅴', 'F': '🅵', 'G': '🅶', 'H': '🅷', 'I': '🅸', 'J': '🅹',
          'K': '🅺', 'L': '🅻', 'M': '🅼', 'N': '🅽', 'O': '🅾', 'P': '🅿', 'Q': '🆀', 'R': '🆁', 'S': '🆂', 'T': '🆃',
          'U': '🆄', 'V': '🆅', 'W': '🆆', 'X': '🆇', 'Y': '🆈', 'Z': '🆉',
          '0': '0⃣', '1': '1⃣', '2': '2⃣', '3': '3⃣', '4': '4⃣', '5': '5⃣', '6': '6⃣', '7': '7⃣', '8': '8⃣', '9': '9⃣'
        };
        return squareMap[char] || char;
      }),
      example: '🆂🆃🆈🅻🅸🆂🅷 🆃🅴🆇🆃',
      icon: <Star className="w-4 h-4" />
    },
    {
      name: 'Upside Down',
      converter: (text) => {
        const flippedMap: { [key: string]: string } = {
          'a': 'ɐ', 'b': 'q', 'c': 'ɔ', 'd': 'p', 'e': 'ǝ', 'f': 'ɟ', 'g': 'ƃ', 'h': 'ɥ', 'i': 'ᴉ', 'j': 'ɾ',
          'k': 'ʞ', 'l': 'l', 'm': 'ɯ', 'n': 'u', 'o': 'o', 'p': 'd', 'q': 'b', 'r': 'ɹ', 's': 's', 't': 'ʇ',
          'u': 'n', 'v': 'ʌ', 'w': 'ʍ', 'x': 'x', 'y': 'ʎ', 'z': 'z',
          'A': '∀', 'B': 'ᗺ', 'C': 'Ɔ', 'D': 'ᗡ', 'E': 'Ǝ', 'F': 'ᖴ', 'G': 'פ', 'H': 'H', 'I': 'I', 'J': 'ſ',
          'K': 'ʞ', 'L': '˥', 'M': 'W', 'N': 'N', 'O': 'O', 'P': 'Ԁ', 'Q': 'Q', 'R': 'ᴿ', 'S': 'S', 'T': '┴',
          'U': '∩', 'V': 'Λ', 'W': 'M', 'X': 'X', 'Y': '⅄', 'Z': 'Z',
          '0': '0', '1': 'Ɩ', '2': 'ᄅ', '3': 'Ɛ', '4': 'ㄣ', '5': 'ϛ', '6': '9', '7': 'ㄥ', '8': '8', '9': '6',
          '?': '¿', '!': '¡', '.': '˙', ',': "'", "'": '‛', '"': '„', '(': ')', ')': '('
        };
        return text.split('').map(char => flippedMap[char] || char).reverse().join('');
      },
      example: 'ʇxǝ┴ ɥsᴉlʎ┴S',
      icon: <Shuffle className="w-4 h-4" />
    },
    {
      name: 'Strikethrough',
      converter: (text) => text.split('').map(char => char + '\u0336').join(''),
      example: 'S̶t̶y̶l̶i̶s̶h̶ ̶T̶e̶x̶t̶',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Underline',
      converter: (text) => text.split('').map(char => char + '\u0332').join(''),
      example: 'S̲t̲y̲l̲i̲s̲h̲ ̲T̲e̲x̲t̲',
      icon: <Type className="w-4 h-4" />
    },
    {
      name: 'Small Caps',
      converter: (text) => text.replace(/[a-z]/g, (char) => {
        const smallCapsMap: { [key: string]: string } = {
          'a': 'ᴀ', 'b': 'ʙ', 'c': 'ᴄ', 'd': 'ᴅ', 'e': 'ᴇ', 'f': 'ꜰ', 'g': 'ɢ', 'h': 'ʜ', 'i': 'ɪ', 'j': 'ᴊ',
          'k': 'ᴋ', 'l': 'ʟ', 'm': 'ᴍ', 'n': 'ɴ', 'o': 'ᴏ', 'p': 'ᴘ', 'q': 'Q', 'r': 'ʀ', 's': 'ꜱ', 't': 'ᴛ',
          'u': 'ᴜ', 'v': 'ᴠ', 'w': 'ᴡ', 'x': 'x', 'y': 'ʏ', 'z': 'ᴢ'
        };
        return smallCapsMap[char] || char;
      }),
      example: 'ꜱᴛʏʟɪꜱʜ ᴛᴇxᴛ',
      icon: <Type className="w-4 h-4" />
    }
  ];

  const generateStyles = () => {
    if (!inputText.trim()) {
      toast.error('Please enter some text');
      return;
    }

    const generated = textStyles.map(style => ({
      style: style.name,
      text: style.converter(inputText)
    }));

    setGeneratedTexts(generated);
    toast.success('Stylish text generated!');
  };

  const copyText = (text: string, styleName: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${styleName} text copied!`);
  };

  const copyAllStyles = () => {
    const allText = generatedTexts.map(item => `${item.style}: ${item.text}`).join('\n\n');
    navigator.clipboard.writeText(allText);
    toast.success('All styles copied!');
  };

  const loadExample = () => {
    setInputText('Stylish Text');
    toast.success('Example text loaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Stylish Text Generator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Create stylish and fancy text for social media, bios, and posts. Generate unique text styles with special Unicode characters.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Section */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Your Text
                </label>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Type your text here..."
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={generateStyles}
                  disabled={!inputText.trim()}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate Styles
                </button>
                <button
                  onClick={loadExample}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Load Example
                </button>
                {generatedTexts.length > 0 && (
                  <button
                    onClick={copyAllStyles}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    Copy All
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Style Previews */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Text Style Previews</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {textStyles.map((style, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    {style.icon}
                    <span className="font-medium text-gray-900">{style.name}</span>
                  </div>
                  <div className="text-lg text-gray-800 font-mono bg-white p-2 rounded border">
                    {style.example}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Generated Results */}
          {generatedTexts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-4 mb-8"
            >
              <h3 className="text-lg font-semibold text-gray-900">Generated Stylish Text</h3>
              {generatedTexts.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{item.style}</span>
                    <button
                      onClick={() => copyText(item.text, item.style)}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" />
                      Copy
                    </button>
                  </div>
                  <div className="text-lg bg-gray-50 p-3 rounded border font-mono break-all">
                    {item.text}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibant text-purple-900 mb-3">Best Uses</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• Social media bios and profiles</li>
                <li>• Instagram and Facebook posts</li>
                <li>• Twitter usernames and bios</li>
                <li>• Discord and gaming usernames</li>
                <li>• Creative writing and art projects</li>
              </ul>
            </div>

            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Tips & Tricks</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Not all platforms support all Unicode styles</li>
                <li>• Test text on your target platform first</li>
                <li>• Combine different styles for unique effects</li>
                <li>• Use sparingly for best visual impact</li>
                <li>• Consider readability and accessibility</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StylishTextGenerator;
